import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ExcelConstant } from '../../../constant/excel.constant';
import { AllowedStoreTypeEnum } from '../../../enum/type/allowed-store-type.enum';
import { DateRangeEnum } from '../../../enum/date-range.enum';
import { RouteEnum } from '../../../enum/route.enum';
import { DynamicTableColumnModel } from '../../../models/dto/core/dynamicTableColumn.model';
import { SelectedItemIdDto } from '../../../models/dto/selectedItemIdDto';
import { SmsLogReportRequest } from '../../../models/rest/log/sms-log-report-request';
import { AuthService } from '../../../services/auth.service';
import { AlertifyService } from '../../../services/core/alertify.service';
import { ExportService } from '../../../services/core/export.service';
import { LogReportService } from '../../../services/report/log-report.service';
import { SmsLogReportResponse } from '../../../models/rest/log/sms-log-report-response';
import { DateFormatEnum } from '../../../enum/date-format.enum';
import { SmsLogReportTypeEnum } from '../../../enum/sms/sms-log-report-type.enum';
import { SmsLogStatusSummaryResponse } from '../../../models/rest/log/sms-log-status-summary-response';
import { SmsSettingService } from '../../../services/sms/sms-setting.service';
import { ColumnVisibleService } from '../../../services/core/column-visible.service';
import { StoreCacheService } from '../../../services/cache/store-cache.service';
import { RoleCodeEnum } from '../../../enum/role-code.enum';
import { ExportDataRequest } from '../../../models/rest/export/export-data-request';
import { ExportFileTypeEnum } from '../../../enum/common/export-file-type.enum';
import { UserOperationalService } from '../../../services/user-operational.service';
import { StoreSettingEntity } from '../../../models/entity/store/store-setting.entity';
import { DateUtils } from '../../../utils/date.utils';
import { ArrayUtils } from '../../../utils/array.utils';
import { ColumnDataTypeEnum } from '../../../enum/common/column-data-type.enum';
import { SmsMessageTypeEnumValues } from '../../../enum/sms/sms-message-type.enum';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-sms-log-report-modal',
  templateUrl: './sms-log-report-modal.component.html',
  styleUrls: ['./sms-log-report-modal.component.css']
})
export class SmsLogReportModalComponent implements OnInit {
  title;
  screenCode = "sms-log-report-modal";
  dataList: SmsLogReportResponse[] = [];
  dataListSummary: SmsLogStatusSummaryResponse[] = [];
  selectedData: SmsLogReportResponse | null = null;
  smsBalance: number = 0;
  searchCriteria: SmsLogReportRequest = new SmsLogReportRequest();
  dateRange: string = DateRangeEnum.TODAY;
  dateTitle: string;
  allowedType: string = AllowedStoreTypeEnum.CUSTOMER;
  reportType: string = SmsLogReportTypeEnum.ALL;
  messageType: string = "";
  cols: DynamicTableColumnModel[];
  selectedColumns: any[];
  loading: boolean = false;
  stores: SelectedItemIdDto[] = [];
  selectedStores: SelectedItemIdDto[] = [];
  storeDropdownSettings = {};
  smsLogReportTypeEnum = SmsLogReportTypeEnum;
  exportFileTypeEnum = ExportFileTypeEnum;
  storeSetting: StoreSettingEntity;
  smsMessageTypeEnumValues = SmsMessageTypeEnumValues;
  searchText: string = "";
  isBusy: boolean = false;

  constructor(
    public activeModal: NgbActiveModal,
    private translate: TranslateService,
    private authService: AuthService,
    private alertifyService: AlertifyService,
    private logReportService: LogReportService,
    private exportService: ExportService,
    private storeCacheService: StoreCacheService,
    private smsSettingService: SmsSettingService,
    private router: Router,
    private userOperationalService: UserOperationalService,
    private columnVisibleService: ColumnVisibleService
  ) { }

  async ngOnInit() {
    this.title = this.translate.instant('menuitem.sms-log-report');
    this.getDropdownSettings();
    this.setStartEndDate();
    this.getCols();
    this.setSelectedColumns();
    await this.getStores();
    await this.getStoreSetting();
    this.getReport();
    this.getSmsBalance();
  }

  private getDropdownSettings() {
    this.storeDropdownSettings = {
      singleSelection: false,
      idField: "id",
      textField: "name",
      placeHolder: this.translate.instant('dropdown.select-store'),
      selectAllText: this.translate.instant('dropdown.select-all-text'),
      unSelectAllText: this.translate.instant('dropdown.un-select-all-text'),
      searchPlaceholderText: this.translate.instant('dropdown.search-placeholder-text'),
      itemsShowLimit: 1,
      allowSearchFilter: true,
    };
  }

  async getStores() {
    try {
      const res = await this.storeCacheService.getStoreSelectList(this.authService.getStoreId(), this.allowedType).toPromise();
      this.stores = res ?? [];
      this.selectedStores = res ?? [];
    } catch (err) {
      this.alertifyService.error(err);
    }
  }

  async getStoreSetting() {
    try {
      const res = await this.storeCacheService.getSetting(this.authService.getStoreId()).toPromise();
      this.storeSetting = res!;
    } catch (err) {
      this.storeSetting = undefined as any;
    }
  }

  setStartEndDate() {
    let returnValue = DateUtils.getStartEndDate(this.dateRange);
    this.searchCriteria.startDate = returnValue.startDate;
    this.searchCriteria.endDate = returnValue.endDate;
    this.searchCriteria.isDateActive = returnValue.isDateActive;
    this.dateTitle = this.dateRange === DateRangeEnum.CUSTOM ? returnValue.title : this.translate.instant('dateRangeEnumValue.' + returnValue.title);
  }

  changedDate(p) {
    this.searchCriteria.startDate = p.startDate;
    this.searchCriteria.endDate = p.endDate;
    this.searchCriteria.isDateActive = p.isDateActive;
    this.dateRange = p.dateRange;
    this.getReport();
  }

  changedReportType(event: any) {
    this.reportType = event;
    this.reportTypeChange();
  }

  reportTypeChange() {
    this.setSelectedColumns();
    this.getReport();
  }

  getReport() {
    this.loading = true;
    this.selectedData = null;
    this.searchCriteria.storeIds = ArrayUtils.getValuesByKey(this.selectedStores, 'id');
    this.searchCriteria.reportType = this.reportType;
    this.searchCriteria.customerId = 0;
    this.searchCriteria.messageType = this.messageType;
    this.logReportService.getSmsLogReport(this.searchCriteria).subscribe(
      data => {
        this.dataList = data;
        this.loading = false;
      },
      err => {
        this.loading = false;
        this.alertifyService.error(err);
      }
    );
    this.logReportService.getSmsLogStatusSummaryReport(this.searchCriteria).subscribe(
      data => this.dataListSummary = data,
      err => { }
    );
  }

  getSmsBalance() {
    if (!this.storeSetting?.smsChannelType) return;
    this.smsSettingService.getSmsBalance(this.authService.getStoreId(), this.storeSetting.smsChannelType).subscribe(
      res => this.smsBalance = res,
      err => {
        this.smsBalance = 0;
        this.alertifyService.error(err);
      }
    );
  }

  async exportFile(fileType: string) {
    try {
      if (! await this.userOperationalService.checkAndOpenModalIfNecessary(RoleCodeEnum.EXPORT_CUSTOMER_LIST, this.authService.getStoreId(), AllowedStoreTypeEnum.ALLOWED_STORE_REPORT)) {
        return;
      }
      let request = new ExportDataRequest();
      request.fileName = this.title;
      request.fileType = fileType;
      request.data = this.dataList;
      request.columns = this.exportService.convertToExportColumns(this.selectedColumns);
      request.customerId = this.searchCriteria.customerId;
      request.isDateActive = this.searchCriteria.isDateActive;
      request.startDate = this.searchCriteria.startDate;
      request.endDate = this.searchCriteria.endDate;
      request.isLandscape = true;
      await this.exportService.exportFile(request);
    } catch (e) {
      this.alertifyService.error(e);
    }
  }

  onRowDblClick(event, rowData) {
    if (rowData.customerId > 0) {
      this.activeModal.close();
      this.router.navigate([`${RouteEnum.CUSTOMER_DETAIL}/${rowData.customerId}`], { skipLocationChange: false });
    }
  }

  updateSmsStatus() {
    if (this.isBusy) return;
    this.isBusy = true;
    this.logReportService.updateSmsState(this.searchCriteria).subscribe(
      data => {
        this.alertifyService.success("Sms durumu güncellendi");
        this.getReport();
        this.isBusy = false;
      },
      err => {
        this.loading = false;
        this.alertifyService.error(err);
        this.isBusy = false;
      }
    );
  }

  goToSendSms() {
    this.activeModal.close();
    this.router.navigate(['/pages/send-sms']);
  }

  private getCols() {
    this.cols = [
      { field: "creationDate", header: this.translate.instant('common.creationDate'), dataType: "date", dateFormat: DateFormatEnum.DATE_TIME_FORMAT_DISPLAY_WITHOUT_SECOND, isBold: true, isSelected: true },
      { field: 'storeName', header: this.translate.instant('store.storeName'), dataType: 'bold', isSelected: false },
      { field: 'messageType', header: this.translate.instant('sms.messageType'), dataType: ColumnDataTypeEnum.TRANSLATE, translateKey: "smsMessageTypeValue", isSelected: true },
      { field: "customerName", isPrivateData: true, header: this.translate.instant('customer.customerName'), excelWidth: 25, excelStyle: ExcelConstant.BOLD_STYLE, isSelected: true },
      { field: "mobilePhone", isPrivateData: true, header: this.translate.instant('communication.mobilePhone'), dataType: "phone", isSelected: true },
      { field: "channelType", header: this.translate.instant('sms.channelType'), isSelected: true },
      { field: "orderCode", header: this.translate.instant('order.orderCode'), isSelected: true },
      { field: "message", header: this.translate.instant('sms.message'), isSelected: true, dataType: "truncate", maxDisplayLength: 100 },
      { field: "senderState", header: this.translate.instant('sms.status'), dataType: "smsStatus", isSelected: true },
      { field: "responseMessage", header: this.translate.instant('sms.responseMessage'), isSelected: false },
      { field: "smsSenderUid", header: this.translate.instant('sms.smsSenderUid'), isSelected: false },
      { field: "userScreenName", header: this.translate.instant('user.userName'), isSelected: false },
    ];
  }

  setSelectedColumns() {
    this.selectedColumns = this.columnVisibleService.prepareSelectedColumns(this.screenCode, this.reportType, this.cols);
  }

  close() {
    this.activeModal.close();
  }
}
